import { z } from "zod";

import { eventSchema } from "../shared";

// Resume CRUD Events
export const resumeCreatedEvent = eventSchema.extend({
  event: z.string().default("resume_created"),
  properties: z.object({
    resumeId: z.string(),
    title: z.string(),
    visibility: z.enum(["public", "private"]),
    method: z.enum(["create", "sample"]),
  }),
});

export const resumeImportedEvent = eventSchema.extend({
  event: z.string().default("resume_imported"),
  properties: z.object({
    resumeId: z.string(),
    title: z.string(),
    hasJobDescription: z.boolean(),
    titleGenerated: z.boolean(),
  }),
});

export const resumeUpdatedEvent = eventSchema.extend({
  event: z.string().default("resume_updated"),
  properties: z.object({
    resumeId: z.string(),
    fieldsUpdated: z.array(z.string()),
  }),
});

export const resumeDeletedEvent = eventSchema.extend({
  event: z.string().default("resume_deleted"),
  properties: z.object({
    resumeId: z.string(),
    title: z.string(),
  }),
});

export const resumeLockedEvent = eventSchema.extend({
  event: z.string().default("resume_locked"),
  properties: z.object({
    resumeId: z.string(),
    locked: z.boolean(),
  }),
});

// Resume Access Events
export const resumeViewedEvent = eventSchema.extend({
  event: z.string().default("resume_viewed"),
  properties: z.object({
    resumeId: z.string(),
    username: z.string(),
    slug: z.string(),
    isPublic: z.boolean(),
  }),
});

export const resumeStatisticsViewedEvent = eventSchema.extend({
  event: z.string().default("resume_statistics_viewed"),
  properties: z.object({
    resumeId: z.string(),
    views: z.number(),
    downloads: z.number(),
  }),
});

// Resume Print/Export Events
export const resumePrintedEvent = eventSchema.extend({
  event: z.string().default("resume_printed"),
  properties: z.object({
    resumeId: z.string(),
    isPreview: z.boolean(),
  }),
});

export const resumeDownloadedEvent = eventSchema.extend({
  event: z.string().default("resume_downloaded"),
  properties: z.object({
    resumeId: z.string(),
    username: z.string(),
    slug: z.string(),
  }),
});

// PDF Parsing Events
export const pdfParsingStartedEvent = eventSchema.extend({
  event: z.string().default("pdf_parsing_started"),
  properties: z.object({
    pdfSize: z.number().optional(),
  }),
});

export const pdfParsingCompletedEvent = eventSchema.extend({
  event: z.string().default("pdf_parsing_completed"),
  properties: z.object({
    success: z.boolean(),
    processingTime: z.number().optional(),
    errorMessage: z.string().optional(),
  }),
});

// Resume Title Generation Events
export const resumeTitleGeneratedEvent = eventSchema.extend({
  event: z.string().default("resume_title_generated"),
  properties: z.object({
    originalTitle: z.string().optional(),
    generatedTitle: z.string(),
    hasJobDescription: z.boolean(),
    success: z.boolean(),
  }),
});

// Type exports
export type ResumeCreatedEvent = z.infer<typeof resumeCreatedEvent>;
export type ResumeImportedEvent = z.infer<typeof resumeImportedEvent>;
export type ResumeUpdatedEvent = z.infer<typeof resumeUpdatedEvent>;
export type ResumeDeletedEvent = z.infer<typeof resumeDeletedEvent>;
export type ResumeLockedEvent = z.infer<typeof resumeLockedEvent>;
export type ResumeViewedEvent = z.infer<typeof resumeViewedEvent>;
export type ResumeStatisticsViewedEvent = z.infer<typeof resumeStatisticsViewedEvent>;
export type ResumePrintedEvent = z.infer<typeof resumePrintedEvent>;
export type ResumeDownloadedEvent = z.infer<typeof resumeDownloadedEvent>;
export type PdfParsingStartedEvent = z.infer<typeof pdfParsingStartedEvent>;
export type PdfParsingCompletedEvent = z.infer<typeof pdfParsingCompletedEvent>;
export type ResumeTitleGeneratedEvent = z.infer<typeof resumeTitleGeneratedEvent>;
